import { Icon, SectionHeader } from "../../components/public/ui";

const smallCards = [
  {
    icon: "laptop_mac",
    iconBox: "bg-secondary-fixed text-secondary",
    title: "Convenient Browser Learning",
    body: "No bloated desktop software or plug-ins. Access your full practice dashboard securely from Windows, macOS, or Chromebook.",
    footer: "Cloud Synced Instantly →",
    footerColor: "text-secondary",
  },
  {
    icon: "fact_check",
    iconBox: "bg-tertiary-fixed text-tertiary",
    title: "Authentic CELPIP® Questions",
    body: "Carefully researched question prompts that mirror real immigration test standards and grading criteria.",
    footer: "Regularly Updated Bank →",
    footerColor: "text-tertiary",
  },
  {
    icon: "do_not_disturb_on",
    iconBox: "bg-primary-fixed-dim text-primary",
    title: "Distraction-Free Focus",
    body: "Clean UI with minimal visual clutter, keeping your cognitive focus solely on processing English syntax and test speed.",
    footer: "Exam Mode Toggle →",
    footerColor: "text-primary",
  },
];

const skillChips = [
  "Grammar Accuracy",
  "Lexical Resource",
  "Task Fulfillment",
  "Pronunciation Cadence",
];

const SmallCard = ({ card }: { card: (typeof smallCards)[number] }) => (
  <div className="bg-surface-container-lowest p-8 rounded-3xl shadow-sm flex flex-col justify-between">
    <div className="flex flex-col gap-3">
      <span
        className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.iconBox}`}
      >
        <Icon name={card.icon} />
      </span>
      <h3 className="font-headline-sm text-headline-sm text-on-surface">
        {card.title}
      </h3>
      <p className="font-body-sm text-body-sm text-on-surface-variant">
        {card.body}
      </p>
    </div>
    <span
      className={`font-label-sm text-label-sm font-bold pt-4 ${card.footerColor}`}
    >
      {card.footer}
    </span>
  </div>
);

export const Benefits = () => (
  <section className="w-full py-20 bg-surface">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <SectionHeader
        eyebrow="Engineered for Success"
        title="Built for Learners Who Want to Succeed"
        description="Every design choice and feature is calibrated to eliminate exam anxiety and maximize score potential."
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Wide card */}
        <div className="md:col-span-2 bg-gradient-to-br from-surface-container-lowest to-surface-container-low p-8 rounded-3xl shadow-sm flex flex-col justify-between">
          <div className="flex flex-col gap-3">
            <span className="w-10 h-10 rounded-xl bg-primary-fixed text-primary flex items-center justify-center">
              <Icon name="calendar_month" />
            </span>
            <h3 className="font-headline-md text-headline-md text-on-surface">
              Flexible, Self-Paced Preparation
            </h3>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-md">
              Whether you have 2 weeks or 6 months before your test, practice
              whenever it suits you and pick up exactly where you left off.
            </p>
          </div>
          <div className="mt-6 p-4 bg-surface-container-lowest rounded-xl flex flex-wrap gap-2 items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="check_circle" className="text-tertiary" />
              <span className="font-label-sm text-label-sm font-semibold text-on-surface">
                Target Study Goal: 30 mins / day
              </span>
            </div>
            <span className="font-label-sm text-label-sm text-primary font-bold">
              On Track (Streak: 12 Days 🔥)
            </span>
          </div>
        </div>

        {smallCards.map((card) => (
          <SmallCard key={card.title} card={card} />
        ))}

        {/* Wide card */}
        <div className="md:col-span-2 bg-gradient-to-tr from-surface-container to-surface-container-lowest p-8 rounded-3xl shadow-sm flex flex-col justify-between">
          <div className="flex flex-col gap-3">
            <span className="w-10 h-10 rounded-xl bg-secondary-fixed text-secondary flex items-center justify-center">
              <Icon name="speed" />
            </span>
            <h3 className="font-headline-md text-headline-md text-on-surface">
              Clear Progress Visualization
            </h3>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-lg">
              Understand exactly how your answers map to Canadian Language
              Benchmarks (CLB 4 through CLB 12) with transparent sub-skill
              ratings.
            </p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {skillChips.map((chip) => (
              <span
                key={chip}
                className="px-3 py-1 rounded-full bg-surface-container-highest text-xs font-semibold text-on-surface"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
